import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchDiarization, executeDiarization } from '../services/diarizationApi.js';
import { DIARIZATION_PHASE } from '../pages/ActivityDetail/diarization/diarizationAdapter.js';

/**
 * `pending` の間に状態を取り直す間隔 (SCR-D-001 §6.4「画面を開いている間」)。
 * 【仮定: 状態の取り直しの間隔は 30 秒 / 確認先=#1246・#1247 で BE・FE が合意 / 追跡=#1247】（SCR-D-001 1.6 の印）
 */
export const DIARIZATION_POLL_INTERVAL_MS = 30000;

const INITIAL_STATE = { phase: DIARIZATION_PHASE.idle, acceptedAt: null, failure: null, result: null };

const isSettledPhase = (phase) => phase === DIARIZATION_PHASE.done || phase === DIARIZATION_PHASE.error;

/**
 * 活動詳細画面の話者分離の状態 (SCR-D-001 DP-D-004・§6.1・§6.4)。
 *
 * - **既存の `processingStatus`（文字起こし・要約・FB の同期処理）とは別に持つ**（§6.4「処理中に止めないもの」）
 * - 画面を開いたときに状態を取得し、`pending` の間は一定の間隔で取り直す。`done`・`error` になったら止める
 * - 自動リトライはしない。実行（BTN-D-001 からの受付）と再実行（BTN-D-003）は利用者の操作のときだけ
 *
 * @param {{
 *   meetingId: string,
 *   enabled: boolean,             // フラグ（`/auth/me` の features.diarization）。false なら何も呼ばない
 *   onSettled?: () => void,       // 受付の応答を受け取ったとき・pending から done/error になったときに呼ぶ
 *                                 // （AI ポイントの残量の取り直し。SCR-D-001 §5.2.1「取り直す時機」②③）
 * }} params
 */
export function useDiarization({ meetingId, enabled, onSettled }) {
    const [state, setState] = useState(INITIAL_STATE);
    const [executeError, setExecuteError] = useState(null); // {message, retryable} | null
    const [isExecuting, setIsExecuting] = useState(false);

    // 直前の状態・呼出側の関数は ref で持つ（状態の更新関数の中で副作用を起こさないため）
    const stateRef = useRef(INITIAL_STATE);
    const onSettledRef = useRef(onSettled);
    useEffect(() => { onSettledRef.current = onSettled; }, [onSettled]);

    const commit = useCallback((next) => {
        stateRef.current = next;
        setState(next);
    }, []);

    const load = useCallback(async () => {
        const res = await fetchDiarization(meetingId);
        // 取得の失敗では表示を変えない（次の取り直しで回復する。画面を落とさない）
        if (!res.ok) return res;
        const wasPending = stateRef.current.phase === DIARIZATION_PHASE.pending;
        commit(res.state);
        if (wasPending && isSettledPhase(res.state.phase) && onSettledRef.current) onSettledRef.current();
        return res;
    }, [meetingId, commit]);

    // 画面を開いたとき（§6.1）
    useEffect(() => {
        if (!enabled || !meetingId) return;
        load();
    }, [enabled, meetingId, load]);

    // pending の間は取り直す（§6.4）。done・error になったら止まる。
    // 取り直しの成否を問わず pollCount を進めて次の予約を立てる（失敗で状態が変わらないと、
    // 状態だけを依存にした予約は立て直されず、待ち受けがそこで止まるため）
    const [pollCount, setPollCount] = useState(0);
    useEffect(() => {
        if (!enabled || state.phase !== DIARIZATION_PHASE.pending) return undefined;
        const timer = setTimeout(async () => {
            await load();
            setPollCount((n) => n + 1);
        }, DIARIZATION_POLL_INTERVAL_MS);
        return () => clearTimeout(timer);
    }, [enabled, state.phase, pollCount, load]);

    /**
     * 実行を受け付けさせる（BTN-D-001 の確認モーダル・BTN-D-003 の再実行）。
     * 受付の間は `loading`。成功なら応答の状態（`pending`）、失敗なら元の状態に戻して executeError を出す。
     */
    const execute = useCallback(async () => {
        if (!enabled || stateRef.current.phase === DIARIZATION_PHASE.loading) return;
        const previous = stateRef.current;
        setIsExecuting(true);
        setExecuteError(null);
        commit({ ...previous, phase: DIARIZATION_PHASE.loading });
        const res = await executeDiarization(meetingId);
        if (res.ok) {
            commit(res.state);
        } else {
            commit(previous);
            setExecuteError({ message: res.errorMessage, retryable: res.retryable === true });
        }
        setIsExecuting(false);
        if (onSettledRef.current) onSettledRef.current();
    }, [enabled, meetingId, commit]);

    const clearExecuteError = useCallback(() => setExecuteError(null), []);

    return { ...state, isExecuting, executeError, clearExecuteError, execute, reload: load };
}
